/** @file Mesh-vertex selection over SelectionSlice.vertices ([attachment id, vertex index] pairs). */
import type { Id } from '@pose2d/format/types';
import type { SelectionSlice } from '../../stores/types';

type Pairs = readonly (readonly [Id, number])[];

/** Pairs of other attachments, then `indices` of `attachment` (sorted, unique). */
function withIndices(pairs: Pairs, attachment: Id, indices: Iterable<number>): [Id, number][] {
  const others = pairs.filter(([a]) => a !== attachment).map(([a, v]): [Id, number] => [a, v]);
  return [...others, ...[...new Set(indices)].sort((x, y) => x - y).map((v): [Id, number] => [attachment, v])];
}

/** Reads and rewrites the vertex part of the selection; `add` keeps other meshes' entries, `replace` drops them. */
export const vertexSelection = {
  /** Sorted unique indices of `attachment` below `count` (stale entries past a smaller mesh are ignored). */
  of(selection: Pick<SelectionSlice, 'vertices'>, attachment: Id, count: number): number[] {
    const picked = selection.vertices.filter(([a, v]) => a === attachment && Number.isInteger(v) && v >= 0 && v < count).map(([, v]) => v);
    return [...new Set(picked)].sort((x, y) => x - y);
  },
  /** Only `indices` of `attachment` (sorted, unique): other meshes' entries are dropped (W3-36, one mesh). */
  replace(selection: Pick<SelectionSlice, 'vertices'>, attachment: Id, indices: Iterable<number>): [Id, number][] {
    return withIndices([], attachment, indices);
  },
  /** `vertices` with `indices` added to (or, with `toggle`, flipped in) the set of `attachment`. */
  add(selection: Pick<SelectionSlice, 'vertices'>, attachment: Id, indices: readonly number[], toggle: boolean): [Id, number][] {
    const set = new Set(selection.vertices.filter(([a]) => a === attachment).map(([, v]) => v));
    for (const v of indices) {
      if (toggle && set.has(v)) set.delete(v);
      else set.add(v);
    }
    return withIndices(selection.vertices, attachment, set);
  },
  /** Overlay pairs [compiled attachment index, vertex] for `OverlaySelection.vertices`. */
  toOverlay(indices: readonly number[], att: number): [number, number][] {
    return indices.map((v): [number, number] => [att, v]);
  },
};
